import { MutableRefObject, useRef } from 'react';
import { useEffect, useState } from 'react';
import {
    PositionInformation,
    getElementPositionInformation,
} from './getElementPositionInformation';

export interface FlyoutPositionRecommendation {
    /**
     * Recommended position to place your element in terms of on which side of your
     * reference element it should be. Currently only supporting 'top' and 'bottom'.
     * 'left' and 'right' is not supported at this moment.
     */
    position: 'top' | 'bottom';

    /**
     * Recommended position to justify your element in terms of which end of the flyout
     * should align with your reference element.
     */
    justify: 'start' | 'end';
}

/**
 * React Hook to support with setting position for different kind of flyouts, e.g.
 * dropdowns, tooltips, popover menus
 * @deprecated Use inlduded third-party library `@floating-ui/react-dom`
 */
export function useElementFlyoutPosition(
    element: MutableRefObject<HTMLElement> | HTMLElement,

    /**
     * Is the flyout opened/visible or not. Is used as a trigger for
     * recalculation to workaround using additional events that could pollute
     * the DOM
     */
    open?: boolean,

    /**
     * Can be set to an element other than the viewport
     */
    viewportElement?: HTMLElement
): FlyoutPositionRecommendation {
    const [position, setPosition] = useState<
        FlyoutPositionRecommendation['position']
    >('bottom');
    const [justify, setJustify] = useState<
        FlyoutPositionRecommendation['justify']
    >('start');

    const prevPositionInfo = useRef<PositionInformation>();

    useEffect(() => {
        const el = isRef(element) ? element.current : element;

        if (el) {
            const positionInfo = getElementPositionInformation(
                el,
                {
                    strict: true,
                },
                viewportElement
            );

            if (
                positionInfo.viewportHeight !==
                    prevPositionInfo.current?.viewportHeight ||
                positionInfo.viewportWidth !==
                    prevPositionInfo.current?.viewportWidth ||
                positionInfo.top !== prevPositionInfo.current?.top ||
                positionInfo.left !== prevPositionInfo.current?.left
            ) {
                // Element is outside of the viewport at the bottom, and has more space available at the top
                if (
                    positionInfo.offsetBottom < 0 &&
                    positionInfo.offsetBottom < positionInfo.top
                ) {
                    setPosition('top');
                }

                // Element is outside of the viewport at the right, and has more space available on the left
                if (
                    positionInfo.offsetRight < 0 &&
                    positionInfo.offsetRight < positionInfo.left
                ) {
                    setJustify('end');
                }

                prevPositionInfo.current = positionInfo;
            }
        }
    }, [element, open, viewportElement]);

    return {
        position,
        justify,
    };
}

function isRef<
    T extends MutableRefObject<HTMLElement> = MutableRefObject<HTMLElement>
>(element: T | HTMLElement): element is T {
    return (element as T)?.current !== undefined;
}
